export type AttributionValue = string | string[];

export interface AttributionSourceValue {
  source: string;
  value: string;
}

export interface ExtractedAttributionId {
  value: string;
  sources: string[];
  all_values: AttributionSourceValue[];
}

export interface AttributionUrlSnapshot {
  captured_at: string;
  url_without_query: string;
  origin: string;
  pathname: string;
  hash: string;
  query: Record<string, AttributionValue>;
  query_keys: string[];
}

export interface AttributionSnapshot {
  captured_at: string;
  storage: "sessionStorage";
  initial_url: AttributionUrlSnapshot | null;
  current_url: AttributionUrlSnapshot | null;
  ids: Record<string, ExtractedAttributionId>;
  cookies: {
    present: string[];
    ids: Record<string, string>;
  };
  browser: Record<string, unknown>;
}

const INITIAL_URL_STORAGE_KEY = "tmrw.attribution.initial_url.v1";

const URL_ATTRIBUTION_KEYS = new Set([
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "gclsrc",
  "gad_source",
  "gad_campaignid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
  "rdt_cid",
  "twclid",
  "sznclid",
  "_gl",
  "_ga",
]);

const ATTRIBUTION_COOKIE_NAMES = new Set([
  "_gcl_aw",
  "FPGCLAW",
  "_gcl_ag",
  "FPGCLAG",
  "_gcl_gb",
  "FPGCLGB",
  "_gcl_au",
  "_fbc",
  "_fbp",
  "_ga",
  "_uetmsclkid",
  "_uetsid",
  "_uetvid",
  "_ttp",
  "ttclid",
  "li_fat_id",
  "rdt_cid",
  "twclid",
  "sznclid",
]);

interface StoredInitialUrl {
  href: string;
  captured_at: string;
}

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function isAttributionParamKey(key: string): boolean {
  return (
    URL_ATTRIBUTION_KEYS.has(key) ||
    key.startsWith("utm_") ||
    key.startsWith("_ga_")
  );
}

function isAttributionCookieName(name: string): boolean {
  return ATTRIBUTION_COOKIE_NAMES.has(name) || name.startsWith("_ga_");
}

function decodeCookieValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function parseCookieString(cookieString: string): Record<string, string> {
  if (!cookieString.trim()) return {};

  return cookieString.split(";").reduce<Record<string, string>>((acc, item) => {
    const [rawName, ...valueParts] = item.trim().split("=");
    if (!rawName) return acc;

    acc[rawName] = decodeCookieValue(valueParts.join("="));
    return acc;
  }, {});
}

function parseQuery(searchParams: URLSearchParams): Record<string, AttributionValue> {
  return Array.from(searchParams.entries()).reduce<Record<string, AttributionValue>>(
    (acc, [key, value]) => {
      const previous = acc[key];

      if (Array.isArray(previous)) {
        previous.push(value);
      } else if (typeof previous === "string") {
        acc[key] = [previous, value];
      } else {
        acc[key] = value;
      }

      return acc;
    },
    {},
  );
}

export function createUrlSnapshot(
  href: string,
  capturedAt = new Date().toISOString(),
): AttributionUrlSnapshot | null {
  try {
    const url = new URL(href, "https://tmrw.marketing");
    const query = parseQuery(url.searchParams);
    const hash = url.hash || "";

    return {
      captured_at: capturedAt,
      url_without_query: `${url.origin}${url.pathname}${hash}`,
      origin: url.origin,
      pathname: url.pathname,
      hash,
      query,
      query_keys: Object.keys(query),
    };
  } catch {
    return null;
  }
}

export function initializeAttributionSession(): void {
  if (!isBrowser()) return;

  try {
    const existing = window.sessionStorage.getItem(INITIAL_URL_STORAGE_KEY);
    if (existing) return;

    const initialUrl: StoredInitialUrl = {
      href: window.location.href,
      captured_at: new Date().toISOString(),
    };

    window.sessionStorage.setItem(
      INITIAL_URL_STORAGE_KEY,
      JSON.stringify(initialUrl),
    );
  } catch {
    // Browsers can block storage. In that case we still read current URL on submit.
  }
}

function readInitialUrlFromSession(): StoredInitialUrl | null {
  if (!isBrowser()) return null;

  try {
    const raw = window.sessionStorage.getItem(INITIAL_URL_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredInitialUrl>;
    if (!parsed.href || !parsed.captured_at) return null;

    return {
      href: parsed.href,
      captured_at: parsed.captured_at,
    };
  } catch {
    return null;
  }
}

export function addExtractedId(
  ids: Record<string, ExtractedAttributionId>,
  key: string,
  value: string | null | undefined,
  source: string,
): void {
  const normalized = value?.trim();
  if (!normalized) return;

  const existing = ids[key];
  const sourceValue = { source, value: normalized };

  if (!existing) {
    ids[key] = {
      value: normalized,
      sources: [source],
      all_values: [sourceValue],
    };
    return;
  }

  existing.all_values.push(sourceValue);

  if (!existing.sources.includes(source)) {
    existing.sources.push(source);
  }
}

export function addUrlAttributionIds(
  ids: Record<string, ExtractedAttributionId>,
  snapshot: AttributionUrlSnapshot | null,
  sourcePrefix: "initial_url" | "current_url" | "server_referer",
): void {
  if (!snapshot) return;

  Object.entries(snapshot.query).forEach(([key, value]) => {
    if (!isAttributionParamKey(key)) return;

    if (Array.isArray(value)) {
      value.forEach((item) =>
        addExtractedId(ids, key, item, `${sourcePrefix}.query.${key}`),
      );
      return;
    }

    addExtractedId(ids, key, value, `${sourcePrefix}.query.${key}`);
  });
}

function parseGoogleClickIdCookieValue(value: string): string {
  const classicValue = value.match(/^(?:GCL|1)\.\d+\.([\w-]+)/i)?.[1];
  if (classicValue) return classicValue;

  const keyedValue = value.match(/^2\.\d+\.k([^$]+)(?:\$|$)/)?.[1];
  if (keyedValue) return keyedValue;

  return value;
}

function parseMetaClickIdCookieValue(value: string): string {
  return value.match(/^fb\.\d+\.\d+\.(.+)$/)?.[1] || value;
}

function parseGoogleAnalyticsClientId(value: string): string {
  return value.match(/^GA\d+\.\d+\.(.+)$/)?.[1] || value;
}

function cookieIdName(cookieName: string): string | null {
  if (cookieName === "_gcl_aw" || cookieName === "FPGCLAW") return "gclid";
  if (cookieName === "_gcl_ag" || cookieName === "FPGCLAG") return "gbraid";
  if (cookieName === "_gcl_gb" || cookieName === "FPGCLGB") return "wbraid";
  if (cookieName === "_gcl_au") return "gcl_au";
  if (cookieName === "_fbc") return "fbclid";
  if (cookieName === "_fbp") return "fbp";
  if (cookieName === "_ga") return "ga_client_id";
  if (cookieName.startsWith("_ga_")) {
    return `ga_session_${cookieName.slice(4).toLowerCase()}`;
  }
  if (cookieName === "_uetmsclkid") return "msclkid";
  if (cookieName === "_uetsid") return "uetsid";
  if (cookieName === "_uetvid") return "uetvid";
  if (cookieName === "_ttp") return "ttp";

  if (
    cookieName === "ttclid" ||
    cookieName === "li_fat_id" ||
    cookieName === "rdt_cid" ||
    cookieName === "twclid" ||
    cookieName === "sznclid"
  ) {
    return cookieName;
  }

  return null;
}

function normalizeCookieIdValue(cookieName: string, value: string): string {
  if (
    cookieName === "_gcl_aw" ||
    cookieName === "FPGCLAW" ||
    cookieName === "_gcl_ag" ||
    cookieName === "FPGCLAG" ||
    cookieName === "_gcl_gb" ||
    cookieName === "FPGCLGB"
  ) {
    return parseGoogleClickIdCookieValue(value);
  }

  if (cookieName === "_fbc") {
    return parseMetaClickIdCookieValue(value);
  }

  if (cookieName === "_ga") {
    return parseGoogleAnalyticsClientId(value);
  }

  return value;
}

function addCookieAttributionIds(
  ids: Record<string, ExtractedAttributionId>,
  cookies: Record<string, string>,
): Record<string, string> {
  return Object.entries(cookies).reduce<Record<string, string>>(
    (acc, [cookieName, value]) => {
      if (!isAttributionCookieName(cookieName)) return acc;

      const idName = cookieIdName(cookieName);
      if (!idName) return acc;

      const normalizedValue = normalizeCookieIdValue(cookieName, value);
      acc[idName] = normalizedValue;
      addExtractedId(ids, idName, normalizedValue, `cookie.${cookieName}`);

      return acc;
    },
    {},
  );
}

function getBrowserContext(): Record<string, unknown> {
  if (!isBrowser()) return {};

  return {
    language: window.navigator.language,
    languages: window.navigator.languages,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    user_agent: window.navigator.userAgent,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      pixel_ratio: window.devicePixelRatio,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

export function buildAttributionSnapshot(): AttributionSnapshot | null {
  if (!isBrowser()) return null;

  initializeAttributionSession();

  const capturedAt = new Date().toISOString();
  const initialUrl = readInitialUrlFromSession();
  const initialUrlSnapshot = initialUrl
    ? createUrlSnapshot(initialUrl.href, initialUrl.captured_at)
    : null;
  const currentUrlSnapshot = createUrlSnapshot(window.location.href, capturedAt);
  const cookies = parseCookieString(document.cookie);
  const attributionCookies = Object.fromEntries(
    Object.entries(cookies).filter(([name]) => isAttributionCookieName(name)),
  );
  const ids: Record<string, ExtractedAttributionId> = {};

  addUrlAttributionIds(ids, initialUrlSnapshot, "initial_url");
  addUrlAttributionIds(ids, currentUrlSnapshot, "current_url");

  const cookieIds = addCookieAttributionIds(ids, attributionCookies);

  return {
    captured_at: capturedAt,
    storage: "sessionStorage",
    initial_url: initialUrlSnapshot,
    current_url: currentUrlSnapshot,
    ids,
    cookies: {
      present: Object.keys(attributionCookies),
      ids: cookieIds,
    },
    browser: getBrowserContext(),
  };
}
